/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-10-10 16:03:18
 *
 *******************************************************/
// @flow

import _ from 'lodash';

import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  Page,
  Layout,
  Card,
  Icon,
  DataTable,
  TextField,
  Pagination,
  SkeletonDisplayText,
  TextStyle
} from '@shopify/polaris';
import { SearchMinor } from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { URL } from 'src/constants/links';
import { PRIORITY_LEVEL, NUMBER_SUGGESTION_PER_PAGE } from 'src/constants/suggestion-dictionary';
import { prettyText } from 'src/helpers';

import DidYouKnow from 'src/components/views/suggestion-dictionary/did-you-know';
import ActionsGroupOnListing from 'src/components/views/suggestion-dictionary/actions-group-on-listing';
import NoSuggestions, {
  NotFoundSuggestions
} from 'src/components/views/suggestion-dictionary/no-suggestions';
import ImportModal from './import';
import ExportModal from './export';
import TypeFormEmbeded from 'src/components/views/common/typeform-embedded';
import YoutubeModal from 'src/components/views/common/youtube-modal';
import TitleHelp from 'src/components/views/common/title-help';

import * as suggestionDictionaryActions from 'src/stores/actions/suggestion-dictionary-actions';
import * as checklistActions from 'actions/dashboard/checklist-actions';
import { routes } from 'src/routes';
import history from 'src/routes/history';

type Props = {
  getSuggestions: (q: string, skip: number, limit: number) => void,
  updateSuggestion: () => void,
  getChecklist: () => void,
  suggestions: Array<Object>,
  total: number,
  loading: boolean,
  success: boolean,
  updateSuccess: boolean,
  successFlag: number,
  selectedPlan: string
};

type ComposedProps = Props & WithI18nProps;

type State = {
  activeImportModal: boolean,
  activeExportModal: boolean,
  inputSearchText: string,
  skip: number,
  limit: number,
  firstPageLoading: boolean,
  hasPrevious: boolean,
  hasNext: boolean,
  searchEnabled: Object,
  fromSuggestionPageState: number,
  youtubeModal: boolean,
  suggestionDisplayCheck: boolean
};

const renderRowsLoadingFilter = count => {
  let rows = [];
  for (var i = 0; i < count; i++) {
    rows.push([
      <SkeletonDisplayText size="small" />,
      <SkeletonDisplayText size="small" />,
      <SkeletonDisplayText size="small" />
    ]);
  }

  return rows;
};

class SuggestionDictionary extends Component<ComposedProps, State> {
  timerTyping = undefined;

  state = {
    // Import modal
    activeImportModal: false,

    // Export modal
    activeExportModal: false,

    // Search string
    inputSearchText: '',

    skip: 0,
    limit: NUMBER_SUGGESTION_PER_PAGE,
    firstPageLoading: true,
    hasPrevious: false,
    hasNext: true,

    // Control search enable for suggestions
    searchEnabled: {},

    fromSuggestionPageState: 0,
    youtubeModal: false,

    suggestionDisplayCheck: false
  };

  componentDidMount() {
    // List filter tree for first page, get 12 filter trees: 1 default filter, 1 search filter, 10 custom filter
    const { skip, limit } = this.state;
    this.props.getSuggestions('', skip, limit);

    // Show typeform when go back from new/edit page
    const fromPage = _.get(this.props.location, 'state.fromPage', '');
    if (!_.isEmpty(fromPage)) {
      const { fromSuggestionPageState } = this.state;
      this.setState({ fromSuggestionPageState: fromSuggestionPageState + 1 });
    }
  }

  componentWillReceiveProps(nextProps) {
    const { skip, limit } = this.state;
    const { total, success, updateSuccess, suggestions, successFlag } = nextProps;
    const { successFlag: successFlagPrev } = this.props;

    const hasNext = skip + limit < total;
    const hasPrevious = skip > 0;
    let updatedState = { hasNext, hasPrevious };

    if ((success || updateSuccess) && successFlag !== successFlagPrev) {
      const searchEnabled = this.collectSearchEnabled(suggestions);
      updatedState = _.assign(updatedState, { searchEnabled });
    }
    this.setState(updatedState);
  }

  collectSearchEnabled = suggestions => {
    const status = {};
    suggestions.forEach(suggestion => {
      const { id, searchEnabled } = suggestion;
      status[id] = searchEnabled;
    });

    return status;
  };

  render() {
    const { i18n, loading, suggestions, updateSuccess, selectedPlan } = this.props;
    const {
      skip,
      limit,
      inputSearchText,
      hasPrevious,
      hasNext,
      fromSuggestionPageState,
      youtubeModal
      // suggestionDisplayCheck
    } = this.state;

    // Render suggestions on table
    let rowSuggestions;
    if (!loading) {
      rowSuggestions = this.buildRows();
    } else {
      const numberRender = _.isEmpty(suggestions) ? 5 : suggestions.length;
      rowSuggestions = renderRowsLoadingFilter(numberRender);
    }

    // Check having suggestion
    const havingSuggestions = !_.isEmpty(suggestions);
    const isEmptyFilter = inputSearchText && !havingSuggestions;
    return (
      <div className="BC-Page BC-Page__SuggestionDictionary">
        <Page
          title={i18n.translate('AdminHTML.SuggestionDictionary.SuggestionDictionary.title')}
          primaryAction={
            (havingSuggestions || isEmptyFilter) && (
              <div className="BC-Page BC-Page__Button">
                <ActionsGroupOnListing
                  hideEdit={isEmptyFilter}
                  q={inputSearchText}
                  skip={skip}
                  limit={limit}
                />
              </div>
            )
          }>
          <TitleHelp
            i18n={i18n}
            onPlay={() => this.toggleModal('youtubeModal')}
            hrefUserManual={URL['Link_V2_Search_SuggestionDictionary_ManualGuide']}
            onAnalytics={() => history.push(routes.analytics.overviewSearch())}
            onImport={this.handleChangeImportModal}
            onExport={this.handleChangeExportModal}
          />

          <Layout>
            <Layout.Section>
              <div className="BC-Container__Notifications">
                {/* New feature0423: old customer flow - migrate - Will apply for all store */}
                {/* Did you know? */}
                <DidYouKnow />
              </div>
            </Layout.Section>

            {/* List of suggestion */}
            <Layout.Section>
              <div className="suggestion-list">
                <Card>
                  <Card.Section>
                    <div className="BC__Search-header">
                      <TextStyle variation="strong">
                        {i18n.translate(
                          'AdminHTML.SuggestionDictionary.SuggestionDictionary.listTitle'
                        )}
                      </TextStyle>
                      <div
                        className="search"
                        onKeyDown={this.handleKeyDown}
                        onKeyUp={this.handleKeyUp}>
                        <TextField
                          onChange={this.onSearchSuggestions}
                          value={this.state.inputSearchText}
                          prefix={<Icon source={SearchMinor} color="base" />}
                          placeholder={i18n.translate(
                            'AdminHTML.SuggestionDictionary.SuggestionDictionary.inputSearchText'
                          )}
                        />
                      </div>
                    </div>
                  </Card.Section>
                  <Card.Section>
                    <DataTable
                      verticalAlign="middle"
                      columnContentTypes={['text', 'text', 'text']}
                      headings={[
                        i18n.translate(
                          'AdminHTML.SuggestionDictionary.SuggestionDictionary.status'
                        ),
                        i18n.translate(
                          'AdminHTML.SuggestionDictionary.SuggestionDictionary.suggestion'
                        ),
                        i18n.translate(
                          'AdminHTML.SuggestionDictionary.SuggestionDictionary.priority'
                        )
                      ]}
                      rows={rowSuggestions}
                    />
                  </Card.Section>
                </Card>
              </div>

              {(havingSuggestions || isEmptyFilter) && (
                <div className="pagination">
                  {(hasPrevious || hasNext) && havingSuggestions && (
                    <Pagination
                      hasPrevious={hasPrevious}
                      onPrevious={this.getPreviousPage}
                      hasNext={hasNext}
                      onNext={this.getNextPage}
                    />
                  )}
                  <div className="actionsGroup">
                    <ActionsGroupOnListing
                      hideEdit={isEmptyFilter}
                      q={inputSearchText}
                      skip={skip}
                      limit={limit}
                    />
                  </div>
                </div>
              )}
            </Layout.Section>
          </Layout>
        </Page>

        <ImportModal
          active={this.state.activeImportModal}
          handleChange={this.handleChangeImportModal}
          getSuggestionsFirstPage={this.getSuggestionsFirstPage}
          history={history}
        />

        <ExportModal
          active={this.state.activeExportModal}
          handleChange={this.handleChangeExportModal}
        />

        <YoutubeModal
          active={youtubeModal}
          embeddedURL={URL['Link_V2_Search_SuggestionDictionary_Video']}
          onClose={() => this.toggleModal('youtubeModal')}
          title={i18n.translate(
            'AdminHTML.SuggestionDictionary.SuggestionDictionary.youtubeModal_Title'
          )}
        />

        {!_.isEmpty(selectedPlan) && (
          <TypeFormEmbeded
            type="suggestion-dictionary"
            conditionShow={updateSuccess}
            conditionShowStateChange={fromSuggestionPageState}
          />
        )}
      </div>
    );
  }

  buildRows = () => {
    const { i18n, suggestions } = this.props;
    let rows = [];
    if (!_.isEmpty(suggestions)) {
      _.forEach(suggestions, suggestion => {
        const { id, term, priority } = suggestion;
        const priorityValue = _.find(PRIORITY_LEVEL, item => item.value === priority);
        const priorityLabel = priorityValue ? i18n.translate(priorityValue.label) : '';

        if (term) {
          rows.push([this.renderCheckbox(id), <span>{term} </span>, <span>{priorityLabel} </span>]);
        } else {
          rows.push([
            <SkeletonDisplayText size="small" />,
            <SkeletonDisplayText size="small" />,
            <SkeletonDisplayText size="small" />
          ]);
        }
      });
    } else {
      rows.push([
        '',
        this.state.inputSearchText ? <NotFoundSuggestions i18n={i18n} /> : <NoSuggestions />,
        ''
      ]);
    }

    return rows;
  };

  renderCheckbox = id => {
    const { searchEnabled } = this.state;
    return (
      <label className="BC-Checkbox-Status">
        <input
          type="checkbox"
          checked={searchEnabled[id] || false}
          onChange={() => this.handleChangeStatus(id)}
        />
        <span />
      </label>
    );
  };

  // Key press event on search field
  handleKeyDown = event => {
    clearTimeout(this.timerTyping);
  };

  // Key up event, start searching after 500ms
  handleKeyUp = event => {
    const charList = 'abcdefghijklmnopqrstuvwxyz0123456789';
    const key = event.key.toLowerCase();
    const keyCode = event.keyCode;

    if (charList.indexOf(key) === -1 && keyCode !== 8) {
      return;
    }

    // Invoke search request after 500ms
    this.timerTyping = setTimeout(() => {
      this.handleSearch();
    }, 500);
  };

  // On search text change
  onSearchSuggestions = inputSearchText => {
    this.setState({ inputSearchText });
  };

  // Search suggestions
  handleSearch = () => {
    let inputSearchText = this.state.inputSearchText.toLowerCase().trim();
    inputSearchText = prettyText(inputSearchText);

    // Having search text, search custom filter for page 1
    if (inputSearchText) {
      // Set limit per page
      this.setState({ skip: 0, limit: NUMBER_SUGGESTION_PER_PAGE });

      this.props.getSuggestions(inputSearchText, 0, NUMBER_SUGGESTION_PER_PAGE);
    } else {
      // Search text is empty, get all filter tree for page 1
      const skip = 0,
        limit = NUMBER_SUGGESTION_PER_PAGE;

      // Set skip, limit for page 1
      this.setState({ skip, limit });

      // Get filter trees
      this.props.getSuggestions('', skip, limit);
    }
  };

  // Enable/disable suggestion term
  handleChangeStatus = async id => {
    const { searchEnabled } = this.state;
    const { suggestions, updateSuggestion, getChecklist } = this.props;

    // Update status
    const newStatus = !searchEnabled[id];
    searchEnabled[id] = newStatus;

    // Update suggestion
    let suggestion = _.find(suggestions, { id });
    suggestion.searchEnabled = newStatus;
    await updateSuggestion([suggestion], 'changeStatus');
    await getChecklist();
    this.setState({ searchEnabled });
  };

  // Go to previous page
  getPreviousPage = () => {
    const { skip, limit, inputSearchText } = this.state;
    const previousSkip = skip - limit;
    const previousLimit = NUMBER_SUGGESTION_PER_PAGE;

    // Prevent multiple click that the skip number is less than 0
    if (previousSkip < 0) return;

    // Set skip, limit for next page
    this.setState({ skip: previousSkip, limit: previousLimit });
    this.props.getSuggestions(inputSearchText || '', previousSkip, previousLimit);
  };

  // Go to next page
  getNextPage = () => {
    const { skip, limit, inputSearchText } = this.state;
    const { total } = this.props;
    const nextSkip = skip + limit;

    // Prevent multiple click that the skip number is bigger than total
    if (nextSkip >= total) return;

    // Set skip, limit for next page and disable flag for first page loading, don't need to display skeleton loading
    this.setState({ skip: nextSkip, limit });
    this.props.getSuggestions(inputSearchText || '', nextSkip, NUMBER_SUGGESTION_PER_PAGE);
  };

  //
  getSuggestionsFirstPage = () => {
    this.setState({ inputSearchText: '', skip: 0, limit: NUMBER_SUGGESTION_PER_PAGE });
    this.props.getSuggestions('', 0, NUMBER_SUGGESTION_PER_PAGE);
  };

  // Hide/show import modal
  handleChangeImportModal = () => {
    this.setState(({ activeImportModal }) => ({
      activeImportModal: !activeImportModal
    }));
  };

  // Hide/show export modal
  handleChangeExportModal = () => {
    this.setState(({ activeExportModal }) => ({
      activeExportModal: !activeExportModal
    }));
  };

  toggleModal = fieldName => {
    const value = this.state[fieldName];
    this.setState({ [fieldName]: !value });
  };

  handleChangeSuggestionDisplay = newChecked => {
    this.setState({
      suggestionDisplayCheck: newChecked
    });
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  getSuggestions: (q, skip, limit) =>
    dispatch(suggestionDictionaryActions.getSuggestions(q, skip, limit, ownProps.i18n)),
  updateSuggestion: (suggestions, actionName) =>
    dispatch(suggestionDictionaryActions.updateSuggestion(suggestions, actionName, ownProps.i18n)),
  getChecklist: () => dispatch(checklistActions.getChecklist(ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  return {
    suggestions: _.get(state.suggestionDictionary, 'suggestions', [{}, {}, {}]),
    total: _.get(state.suggestionDictionary, 'total', 0),
    loading: _.get(state.suggestionDictionary, 'loading', false),
    success: _.get(state.suggestionDictionary, 'success', false),
    updateSuccess: _.get(state.suggestionDictionary, 'updateSuccess', false),
    successFlag: _.get(state.suggestionDictionary, 'successFlag', 0),
    selectedPlan: _.get(state.oauth.tenantApp, 'selectedPlan')
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(SuggestionDictionary));
